// App — top-level layout + the federated MFE surfaces.
//
// The shell composes three remotes and routes the tab set on
// auth.me.role:
//
//   guardian                       → Today / Inbox        (parentApp)
//   school_admin | super_admin     → Dashboard / Compose  (adminApp)
//   teacher                        → Attendance           (classroomApp)
//   gate_staff                     → (kiosk app — out of scope here)
//
// No router library yet — useState plus role-derived view sets stays
// readable until /notices/:id deep linking lands.
//
// Federated imports resolve at runtime from URLs passed to
// vite-plugin-federation; in tests vite.config.ts swaps them for local
// stubs via resolve.alias.

import { lazy, Suspense, useEffect, useState, type ComponentType } from "react";

import {
  Button,
  Segmented,
  Spinner,
  Stack,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import type { Me } from "@sms/sdk";

import { useAuth } from "./auth/AuthProvider";
import { LoginPage } from "./auth/LoginPage";
import { ErrorBoundary } from "./ErrorBoundary";
import { makeT } from "./i18n";
import { OfflineBanner } from "./OfflineBanner";
import { apiClient } from "./sdk";

// Robust federated-import resolver for @originjs/vite-plugin-federation.
// Its remoteEntry runtime resolves an exposed module to a *factory*
// function (not the module namespace directly): the factory returns
// either `module.default` (when only ESM-marker keys are present) or
// `module` (when there are named exports). We must call that factory.
//
// On top of that, the value we end up with can be:
//   - the namespace object   -> use mod[exportName]
//   - the module's default   -> use mod.default
//   - a factory we forgot to call (defensive) -> call it then retry
//
// Trying each shape avoids React error #306 ("Element type invalid")
// whenever federation's defaults shift between plugin versions.
async function resolveMfeExport(
  raw: unknown,
  exportName: string,
): Promise<unknown> {
  let mod = raw as Record<string, unknown> | (() => unknown) | undefined;
  // The expose entry compiles to a factory: () => moduleOrDefault.
  if (typeof mod === "function") {
    const called = (mod as () => unknown)();
    mod = (await Promise.resolve(called)) as Record<string, unknown>;
  }
  if (!mod) return undefined;
  const fromNamed = (mod as Record<string, unknown>)[exportName];
  const fromDefaultNamespace = (
    (mod as Record<string, unknown>).default as Record<string, unknown> | undefined
  )?.[exportName];
  const asDefault = (mod as Record<string, unknown>).default;
  return fromNamed ?? fromDefaultNamespace ?? asDefault;
}

// Props are intentionally `any` here. The strict typing for each remote
// MFE lives in apps/shell/src/remotes.d.ts; call sites consume those
// declarations directly. Trying to thread a generic through `lazy` here
// would force every caller to import the prop type from the remote's
// source tree, which defeats the federation seam. Loose typing at the
// boundary, strict typing at the JSX render.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

function lazyRemote(load: () => Promise<unknown>, exportName: string) {
  return lazy(async () => {
    const raw = await load();
    const Component = (await resolveMfeExport(raw, exportName)) as
      | AnyComponent
      | undefined;
    if (typeof Component !== "function" && typeof Component !== "object") {
      const shape =
        raw === null
          ? "null"
          : typeof raw === "function"
            ? "function"
            : Object.keys(raw as Record<string, unknown>).join(",");
      throw new Error(
        `lazyRemote: '${exportName}' not found. Raw shape: ${shape}`,
      );
    }
    return { default: Component as AnyComponent };
  });
}

const TodayWidget = lazyRemote(() => import("parentApp/TodayWidget"), "TodayWidget");
const InboxView = lazyRemote(() => import("parentApp/InboxView"), "InboxView");
const NoticeDetail = lazyRemote(() => import("parentApp/NoticeDetail"), "NoticeDetail");
const SettingsView = lazyRemote(() => import("parentApp/SettingsView"), "SettingsView");
const DashboardWidget = lazyRemote(() => import("adminApp/DashboardWidget"), "DashboardWidget");
const ComposeNoticeForm = lazyRemote(() => import("adminApp/ComposeNoticeForm"), "ComposeNoticeForm");
const NoticeReachView = lazyRemote(() => import("adminApp/NoticeReachView"), "NoticeReachView");
const RosterImportView = lazyRemote(() => import("adminApp/RosterImportView"), "RosterImportView");
const AuditLogView = lazyRemote(() => import("adminApp/AuditLogView"), "AuditLogView");
const DevicePairView = lazyRemote(() => import("adminApp/DevicePairView"), "DevicePairView");
const PendingOTPsView = lazyRemote(() => import("adminApp/PendingOTPsView"), "PendingOTPsView");
const AttendanceForm = lazyRemote(() => import("classroomApp/AttendanceForm"), "AttendanceForm");
const MonthlyReport = lazyRemote(() => import("classroomApp/MonthlyReport"), "MonthlyReport");

type GuardianTab = "today" | "inbox" | "settings";
type AdminTab = "dashboard" | "compose" | "roster" | "audit" | "devices" | "otps";
type TeacherTab = "attendance" | "report";

export function App() {
  const { auth, signIn, signOut } = useAuth();
  const [locale, setLocale] = useState<Locale>("ne");
  const [guardianTab, setGuardianTab] = useState<GuardianTab>("today");
  const [adminTab, setAdminTab] = useState<AdminTab>("dashboard");
  // Currently-open notice detail. When set, the guardian surface
  // renders NoticeDetail instead of the inbox list. Tapping back
  // (or switching tabs) clears it.
  const [openedNoticeId, setOpenedNoticeId] = useState<string | null>(null);
  // Admin counterpart: after a notice is sent the admin can jump to
  // its reach report. Cleared on tab switch / back.
  const [viewingReachNoticeId, setViewingReachNoticeId] = useState<string | null>(null);
  const t = makeT(locale);

  // Reflect locale on the <html> tag so :lang(ne) CSS rules (Devanagari
  // line-height bump, font fallback) take effect across every MFE.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  if (auth.state === "loading") {
    return (
      <div style={appShellLoadingStyle}>
        <Spinner size={28} label={t("common.loading")} />
      </div>
    );
  }
  if (auth.state === "anon") {
    return (
      <>
        <OfflineBanner locale={locale} />
        <LoginPage
          client={apiClient}
          locale={locale}
          onSignedIn={(me: Me) => signIn(me)}
        />
      </>
    );
  }

  const role = auth.me?.role ?? "guardian";

  return (
    <div style={appShellStyle}>
      <style>{chromeCSS}</style>
      <OfflineBanner locale={locale} />
      <header className="ds-appchrome">
        <div className="ds-appchrome__inner">
          <Stack direction="row" gap={3} align="center">
            <ShellBrandMark />
            <span style={wordmarkStyle}>{t("app.name")}</span>
          </Stack>
          <Stack direction="row" gap={2} align="center">
            <Segmented<Locale>
              ariaLabel="Language"
              value={locale}
              onChange={setLocale}
              options={[
                { value: "ne", label: "नेपाली" },
                { value: "en", label: "EN" },
              ]}
            />
            <Button
              variant="ghost"
              onClick={() => void signOut()}
              aria-label={t("actions.logout")}
            >
              <Stack direction="row" gap={2} align="center">
                <LogoutGlyph />
                <span style={logoutLabelStyle}>{t("actions.logout")}</span>
              </Stack>
            </Button>
          </Stack>
        </div>
      </header>

      <main className="ds-appchrome__main">
        {role === "school_admin" || role === "super_admin" ? (
          <AdminSurface
            tab={adminTab}
            setTab={(next) => {
              setAdminTab(next);
              setViewingReachNoticeId(null);
            }}
            locale={locale}
            t={t}
            viewingReachNoticeId={viewingReachNoticeId}
            onViewReach={setViewingReachNoticeId}
            onCloseReach={() => setViewingReachNoticeId(null)}
          />
        ) : role === "teacher" ? (
          <TeacherSurface locale={locale} t={t} />
        ) : (
          <GuardianSurface
            tab={guardianTab}
            setTab={(next) => {
              setGuardianTab(next);
              setOpenedNoticeId(null);
            }}
            locale={locale}
            t={t}
            openedNoticeId={openedNoticeId}
            onOpenNotice={setOpenedNoticeId}
            onCloseNotice={() => setOpenedNoticeId(null)}
            onLocaleChange={setLocale}
          />
        )}
      </main>
    </div>
  );
}

// ── Brand mark + glyphs ───────────────────────────────────────────────

function ShellBrandMark() {
  // Smaller sibling of LoginPage's BrandMark — same open-book glyph,
  // same brand fill, half the size, fewer effects. Reads as a quiet
  // header chip rather than a hero element.
  return (
    <div style={shellBrandMarkStyle} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 7l10 4 10-4-10-4-10 4z" />
        <path d="M6 9.5v6c0 .5.3.9.8 1.1A12 12 0 0 0 12 18a12 12 0 0 0 5.2-1.4c.5-.2.8-.6.8-1.1v-6" />
      </svg>
    </div>
  );
}

function LogoutGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// Window CustomEvent name dispatched by parent-app's InboxView on
// every successful fetch. Mirrors the contract declared at
// `apps/parent-app/src/sw-protocol.ts`. The string is copied here so
// the shell doesn't have to depend on the parent-app remote at type
// resolution time (federation remotes are runtime-loaded).
const INBOX_UNREAD_EVENT = "sms:inbox-unread";

function GuardianSurface({
  tab,
  setTab,
  locale,
  t,
  openedNoticeId,
  onOpenNotice,
  onCloseNotice,
  onLocaleChange,
}: {
  tab: GuardianTab;
  setTab: (t: GuardianTab) => void;
  locale: Locale;
  t: (k: string) => string;
  openedNoticeId: string | null;
  onOpenNotice: (noticeId: string) => void;
  onCloseNotice: () => void;
  onLocaleChange: (next: Locale) => void;
}) {
  // Unread count fed by parent-app's InboxView via a window
  // CustomEvent — see sw-protocol.ts INBOX_UNREAD_EVENT. The shell
  // doesn't fetch /notices itself; it just listens.
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    const onUnread = (e: Event) => {
      const detail = (e as CustomEvent<{ count: number }>).detail;
      setInboxUnread(detail?.count ?? 0);
    };
    window.addEventListener(INBOX_UNREAD_EVENT, onUnread);
    return () => window.removeEventListener(INBOX_UNREAD_EVENT, onUnread);
  }, []);

  return (
    <SurfaceShell>
      <SurfaceNav>
        <Segmented<GuardianTab>
          ariaLabel="Guardian sections"
          value={tab}
          onChange={setTab}
          options={[
            { value: "today", label: t("nav.today") },
            {
              value: "inbox",
              label: (
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <span>{t("nav.inbox")}</span>
                  <InboxBadge count={inboxUnread} />
                </span>
              ),
              ariaLabel: inboxUnread > 0
                ? `${t("nav.inbox")} (${inboxUnread} new)`
                : t("nav.inbox"),
            },
            { value: "settings", label: t("nav.settings") },
          ]}
        />
      </SurfaceNav>
      <ErrorBoundary locale={locale}>
        <Suspense fallback={<SurfaceFallback label={t("common.loading")} />}>
          {tab === "today" ? (
            <TodayWidget
              locale={locale}
              client={apiClient}
              onJumpToSettings={() => setTab("settings")}
            />
          ) : tab === "settings" ? (
            <SettingsView
              locale={locale}
              client={apiClient}
              onLocaleChange={onLocaleChange}
              onJumpToToday={() => setTab("today")}
            />
          ) : openedNoticeId ? (
            <NoticeDetail
              noticeId={openedNoticeId}
              locale={locale}
              client={apiClient}
              onBack={onCloseNotice}
            />
          ) : (
            <InboxView locale={locale} client={apiClient} onOpen={onOpenNotice} />
          )}
        </Suspense>
      </ErrorBoundary>
    </SurfaceShell>
  );
}

function AdminSurface({
  tab,
  setTab,
  locale,
  t,
  viewingReachNoticeId,
  onViewReach,
  onCloseReach,
}: {
  tab: AdminTab;
  setTab: (t: AdminTab) => void;
  locale: Locale;
  t: (k: string) => string;
  viewingReachNoticeId: string | null;
  onViewReach: (noticeId: string) => void;
  onCloseReach: () => void;
}) {
  // Segmented supports 2-4 pills cleanly. Admin has 5 sections, so we
  // fall back to a scrollable horizontal row of pills using the same
  // visual vocabulary - a tiny bespoke control rather than fighting
  // Segmented's contract.
  type Opt = { value: AdminTab; label: string };
  const opts: Opt[] = [
    { value: "dashboard", label: t("nav.dashboard") },
    { value: "compose",   label: t("nav.compose")   },
    { value: "roster",    label: t("nav.roster")    },
    { value: "audit",     label: t("nav.audit")     },
    { value: "devices",   label: t("nav.devices")   },
    { value: "otps",      label: t("nav.otps")      },
  ];
  return (
    <SurfaceShell>
      <SurfaceNav>
        <PillRow<AdminTab> options={opts} value={tab} onChange={setTab} />
      </SurfaceNav>
      <ErrorBoundary locale={locale}>
        <Suspense fallback={<SurfaceFallback label={t("common.loading")} />}>
          {tab === "dashboard" ? (
            <DashboardWidget locale={locale} client={apiClient} />
          ) : tab === "roster" ? (
            <RosterImportView locale={locale} client={apiClient} />
          ) : tab === "audit" ? (
            <AuditLogView locale={locale} client={apiClient} />
          ) : tab === "devices" ? (
            <DevicePairView locale={locale} client={apiClient} />
          ) : tab === "otps" ? (
            <PendingOTPsView locale={locale} client={apiClient} />
          ) : viewingReachNoticeId ? (
            <NoticeReachView
              noticeId={viewingReachNoticeId}
              locale={locale}
              client={apiClient}
              onBack={onCloseReach}
            />
          ) : (
            <ComposeNoticeForm
              locale={locale}
              client={apiClient}
              onSent={onViewReach}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </SurfaceShell>
  );
}

function TeacherSurface({
  locale,
  t,
}: {
  locale: Locale;
  t: (k: string) => string;
}) {
  const [tab, setTab] = useState<TeacherTab>("attendance");
  return (
    <SurfaceShell>
      <SurfaceNav>
        <Segmented<TeacherTab>
          ariaLabel="Teacher sections"
          value={tab}
          onChange={setTab}
          options={[
            { value: "attendance", label: t("nav.attendance") },
            { value: "report",     label: t("nav.report")     },
          ]}
        />
      </SurfaceNav>
      <ErrorBoundary locale={locale}>
        <Suspense fallback={<SurfaceFallback label={t("common.loading")} />}>
          {tab === "attendance" ? (
            <AttendanceForm locale={locale} client={apiClient} />
          ) : (
            <MonthlyReport locale={locale} client={apiClient} />
          )}
        </Suspense>
      </ErrorBoundary>
    </SurfaceShell>
  );
}

// ── Surface chrome ───────────────────────────────────────────────────

function SurfaceShell({ children }: { children: React.ReactNode }) {
  return <div className="ds-surface">{children}</div>;
}

function SurfaceNav({ children }: { children: React.ReactNode }) {
  return <div className="ds-surface__nav">{children}</div>;
}

function SurfaceFallback({ label }: { label: string }) {
  return (
    <Stack
      gap={2}
      align="center"
      justify="center"
      style={{ padding: tokens.space[10] }}
    >
      <Spinner size={24} label={label} />
    </Stack>
  );
}

// ── PillRow — Segmented-style row that can accept 5+ options ─────────

function PillRow<V extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: V; label: string }>;
  value: V;
  onChange: (v: V) => void;
}) {
  return (
    <div role="tablist" aria-label="Admin sections" className="ds-pillrow">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className="ds-pillrow__pill"
            data-active={active ? "true" : "false"}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Inbox unread badge ───────────────────────────────────────────────

// InboxBadge renders the unread-notice count inside the Inbox tab's
// label. Intentionally NOT a design-system primitive — one consumer,
// scoped here. Promote to a primitive if a second consumer asks for
// it (admin compose unread? gate kiosk pending count?).
//
// count === 0 renders null so the badge disappears when the inbox
// is current. Caps at "99+" — pilot scale doesn't justify a wider
// pill design.
function InboxBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  const text = count > 99 ? "99+" : String(count);
  return (
    <span style={inboxBadgeStyle} aria-hidden="true" data-testid="inbox-badge">
      {text}
    </span>
  );
}

// ── Styles ───────────────────────────────────────────────────────────

const appShellStyle: React.CSSProperties = {
  minHeight: "100dvh",
  background: tokens.color.semantic.bgSubtle,
};

const appShellLoadingStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const shellBrandMarkStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: 10,
  background: tokens.color.brand[500],
  color: tokens.color.neutral[0],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "inset 0 0 0 1.5px rgba(255, 255, 255, 0.14)",
};

const wordmarkStyle: React.CSSProperties = {
  fontSize: tokens.type.size.lg,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.015em",
  // Keep wordmark from overflowing on small phones - hidden below 360.
  whiteSpace: "nowrap",
};

const inboxBadgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 18,
  height: 18,
  padding: "0 6px",
  marginLeft: 6,
  borderRadius: 9,
  background: tokens.color.brand[500],
  color: tokens.color.neutral[0],
  fontSize: 11,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: 0,
  lineHeight: 1,
};

const logoutLabelStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  fontWeight: tokens.type.weight.medium,
};

const chromeCSS = `
.ds-appchrome {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(180%) blur(10px);
  -webkit-backdrop-filter: saturate(180%) blur(10px);
  border-bottom: 1px solid ${tokens.color.semantic.border};
}
.ds-appchrome__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.space[3]};
  padding: ${tokens.space[3]} ${tokens.space[4]};
  max-width: 1100px;
  margin: 0 auto;
}
.ds-appchrome__main {
  display: block;
  max-width: 1100px;
  margin: 0 auto;
}

@media (max-width: 380px) {
  .ds-appchrome__inner > :first-child > :last-child {
    display: none;
  }
}

@media (min-width: 768px) {
  .ds-appchrome__inner { padding: ${tokens.space[3]} ${tokens.space[6]}; }
}

/* Surface = the per-tab panel under the chrome. Wraps the tab nav + the
 * lazy content. */
.ds-surface {
  display: flex;
  flex-direction: column;
}
.ds-surface__nav {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: ${tokens.color.semantic.bgSubtle};
  padding: ${tokens.space[3]} ${tokens.space[4]} ${tokens.space[2]};
  border-bottom: 1px solid ${tokens.color.semantic.border};
}
@media (min-width: 768px) {
  .ds-surface__nav {
    padding: ${tokens.space[4]} ${tokens.space[6]} ${tokens.space[3]};
  }
}

/* PillRow — admin's 5-tab nav. Horizontally scrollable on narrow
 * viewports so all five tabs are still reachable. */
.ds-pillrow {
  display: flex;
  gap: ${tokens.space[1]};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 4px;
  background: #f1f3f5;
  border-radius: 12px;
}
.ds-pillrow::-webkit-scrollbar { display: none; }
.ds-pillrow__pill {
  flex: 0 0 auto;
  padding: ${tokens.space[2]} ${tokens.space[4]};
  border-radius: 10px;
  background: transparent;
  color: ${tokens.color.semantic.textMuted};
  font-weight: ${tokens.type.weight.medium};
  font-size: ${tokens.type.size.sm};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background ${tokens.motion.durBase} ${tokens.motion.easeOut},
    color      ${tokens.motion.durBase} ${tokens.motion.easeOut},
    box-shadow ${tokens.motion.durBase} ${tokens.motion.easeOut};
  min-height: 40px;
  border: 0;
  letter-spacing: -0.005em;
}
.ds-pillrow__pill[data-active="true"] {
  background: ${tokens.color.semantic.surface};
  color: ${tokens.color.semantic.heading};
  font-weight: ${tokens.type.weight.semibold};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 0 0 1px rgba(15, 23, 42, 0.04);
}
`;
